import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/resume/me').then(r => setExisting(r.data.resume)).catch(() => {});
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('Only PDF files accepted'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('File too large (max 5MB)'); return; }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('resume', file);
    try {
      const res = await api.post('/resume/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Resume uploaded and parsed!');
      setExisting(res.data.resume);
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: 'Syne', fontSize: 26, fontWeight: 800, marginBottom: 8 }}>📄 Resume Upload</div>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>Upload your PDF resume. We'll extract and parse it automatically using AI.</p>
      </div>

      {existing && (
        <div className="card" style={{ borderColor: 'rgba(78,205,196,0.3)', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 4 }}>✅ Current Resume</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{existing.filename}</div>
              {existing.parsed?.skills?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {existing.parsed.skills.slice(0, 8).map(s => <span key={s} className="tag tag-purple">{s}</span>)}
                  {existing.parsed.skills.length > 8 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>+{existing.parsed.skills.length - 8} more</span>}
                </div>
              )}
            </div>
            <button className="btn btn-ghost" onClick={() => navigate('/analyze')}>Analyze Now →</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">{existing ? 'Replace Resume' : 'Upload Resume'}</div>
        <div className="card-sub">PDF only, max 5MB. Your file is processed in memory — nothing is stored on disk.</div>

        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => !file && fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--accent)' : file ? 'var(--accent2)' : 'var(--border)'}`,
            borderRadius: 12, padding: '48px 32px', textAlign: 'center', cursor: file ? 'default' : 'pointer',
            background: dragging ? 'rgba(124,106,247,0.05)' : file ? 'rgba(78,205,196,0.05)' : 'transparent',
            transition: 'all 0.2s', position: 'relative'
          }}>
          {!file ? (
            <>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 8 }}>Drop your PDF here</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>or click to browse</div>
              <input ref={fileRef} type="file" accept=".pdf" style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                onChange={e => handleFile(e.target.files[0])} />
            </>
          ) : (
            <>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, color: 'var(--accent2)', marginBottom: 8 }}>{file.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{(file.size / 1024).toFixed(1)} KB</div>
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }}
                style={{ marginTop: 12, background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', padding: '6px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                Remove
              </button>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button className="btn btn-primary" disabled={!file || loading} onClick={handleUpload}>
            {loading ? '⏳ Parsing with AI...' : '🚀 Upload & Parse'}
          </button>
          {existing && <button className="btn btn-ghost" onClick={() => navigate('/analyze')}>Skip → Analyze</button>}
        </div>
      </div>
    </div>
  );
}
